import { useCallback, useId, useMemo, useState } from "react";
import { z } from "zod";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { useSecurityPin } from "@/shared/hooks/useSecurityPin";
import useTranslations from "@/shared/lang/useTranslations";
import { securityPinIssue, type SecurityPinIssue } from "@/shared/utils/securityPin";

export type SecurityPinSetupStep = "create" | "confirm" | "password" | "done";

/** The three steps the person walks through; `done` is the landing, not a step. */
export const SECURITY_PIN_SETUP_STEPS: readonly SecurityPinSetupStep[] = ["create", "confirm", "password"];

export interface UseSecurityPinSetupOptions {
  /** Runs once the server saved the PIN, before the success state shows. */
  onSaved?: () => void;
}

export interface SecurityPinSetupController {
  step: SecurityPinSetupStep;
  /** Which way the last move went, so the incoming step slides in from the right side. */
  direction: "forward" | "back";
  pin: string;
  confirm: string;
  setPin: (value: string) => void;
  setConfirm: (value: string) => void;
  /** Why the typed PIN would be refused, while it is complete; null while typing or when it is fine. */
  pinIssue: Exclude<SecurityPinIssue, "incomplete"> | null;
  /** The server's refusal of the PIN itself (a pattern this mirror does not know), shown on the first step. */
  pinError: string | null;
  confirmError: string | null;
  /** Error above the form that belongs to neither field (TOTP active, staff, network). */
  formError: string | null;
  /** Bumped on every refusal so the field it concerns can shake again, even with the same message. */
  shakeKey: number;
  passwordForm: ReturnType<typeof useZodForm<ReturnType<typeof passwordSchema>>>;
  passwordFormId: string;
  saving: boolean;
  canContinue: boolean;
  next: () => void;
  back: () => void;
  submitPassword: (values: { password: string }) => Promise<void>;
}

const passwordSchema = (required: string) => z.object({ password: z.string().min(1, required) });

/**
 * The PIN creation flow as a controller, so every host (Settings inline, a dialog, the enrolment notice of a
 * step-up action) lays the steps out its own way and puts the main action where its footer is: choose six
 * digits → type them again → confirm with the account password → done.
 *
 * Choosing and confirming advance on their own as soon as the sixth digit is in and the PIN is acceptable, so
 * the fast path is typing twice and entering the password.
 */
export function useSecurityPinSetup({
  onSaved,
}: UseSecurityPinSetupOptions = {}): SecurityPinSetupController {
  const { t } = useTranslations("global");
  const { user } = useAuth();
  const { save, saving } = useSecurityPin();
  const passwordFormId = useId();
  // The user's own phone is one of the first things a PIN is guessed from, so it is checked while typing too.
  const phone = user?.phone_number ?? null;

  const [step, setStep] = useState<SecurityPinSetupStep>("create");
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [pin, setPinState] = useState("");
  const [confirm, setConfirmState] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [shakeKey, setShakeKey] = useState(0);

  const schema = useMemo(() => passwordSchema(t("security_pin_password_required")), [t]);
  const passwordForm = useZodForm(schema, { password: "" });

  const issue = securityPinIssue(pin, { phone });
  const pinIssue = issue === null || issue === "incomplete" ? null : issue;

  const go = useCallback((to: SecurityPinSetupStep, dir: "forward" | "back") => {
    setDirection(dir);
    setStep(to);
  }, []);

  const shake = useCallback(() => setShakeKey((key) => key + 1), []);

  const confirmMatches = useCallback(
    (value: string) => {
      if (value.length < pin.length) return;
      if (value === pin) {
        setConfirmError(null);
        go("password", "forward");
        return;
      }
      setConfirmError(t("security_pin_mismatch"));
      setConfirmState("");
      shake();
    },
    [go, pin, shake, t],
  );

  const setPin = useCallback(
    (value: string) => {
      setPinState(value);
      setPinError(null);
      setFormError(null);
      // The sixth digit of an acceptable PIN moves on by itself; a weak one stays, with the reason under it.
      const nextIssue = securityPinIssue(value, { phone });
      if (nextIssue === null) {
        setConfirmState("");
        setConfirmError(null);
        go("confirm", "forward");
      } else if (nextIssue !== "incomplete") {
        shake();
      }
    },
    [go, phone, shake],
  );

  const setConfirm = useCallback(
    (value: string) => {
      setConfirmState(value);
      if (value.length > 0) setConfirmError(null);
      if (value.length === pin.length) confirmMatches(value);
    },
    [confirmMatches, pin.length],
  );

  const next = useCallback(() => {
    if (step === "create") {
      if (issue === null) go("confirm", "forward");
      else shake();
      return;
    }
    if (step === "confirm") confirmMatches(confirm);
  }, [confirm, confirmMatches, go, issue, shake, step]);

  const back = useCallback(() => {
    setFormError(null);
    if (step === "confirm") {
      setConfirmState("");
      setConfirmError(null);
      go("create", "back");
    } else if (step === "password") {
      setConfirmState("");
      go("confirm", "back");
    }
  }, [go, step]);

  const submitPassword = useCallback(
    async ({ password }: { password: string }) => {
      setFormError(null);
      const failure = await save(pin, password);
      if (!failure) {
        passwordForm.reset({ password: "" });
        go("done", "forward");
        onSaved?.();
        return;
      }
      shake();
      if (failure.field === "password") {
        passwordForm.setError("password", { message: failure.message });
        // The shake remounts the field, so focus goes back to it once the new one is on screen.
        window.requestAnimationFrame(() => passwordForm.setFocus("password"));
        return;
      }
      if (failure.field === "pin") {
        // The server knows a pattern this mirror does not: start over from the first step, saying why.
        setPinState("");
        setConfirmState("");
        setPinError(failure.message);
        go("create", "back");
        return;
      }
      setFormError(failure.message);
    },
    [go, onSaved, passwordForm, pin, save, shake],
  );

  const canContinue =
    step === "create" ? issue === null : step === "confirm" ? confirm.length === pin.length : step !== "done";

  return {
    step,
    direction,
    pin,
    confirm,
    setPin,
    setConfirm,
    pinIssue,
    pinError,
    confirmError,
    formError,
    shakeKey,
    passwordForm,
    passwordFormId,
    saving,
    canContinue,
    next,
    back,
    submitPassword,
  };
}
